
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { CircleDot, Circle } from 'lucide-react';

interface CoverPageProps {
  onComplete: () => void;
}

export const CoverPage = ({ onComplete }: CoverPageProps) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = 3000; // 3 secondes
    const interval = 30; // mise à jour toutes les 30ms
    const steps = duration / interval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep += 1;
      const newProgress = Math.min(100, Math.round((currentStep / steps) * 100));
      setProgress(newProgress);

      if (newProgress >= 100) {
        clearInterval(timer);
        setIsExiting(true);
        
        // Attendre la fin de l'animation de sortie
        setTimeout(() => {
          onComplete();
        }, 800); // 800ms pour l'animation de sortie
      }
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div
      className={cn(
        'fixed inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 to-primary z-50 transition-opacity duration-800',
        isExiting ? 'opacity-0' : 'opacity-100'
      )}
    >
      <div className={cn(
        'transform transition-all duration-500',
        isExiting ? 'scale-110 opacity-0' : 'scale-100 opacity-100'
      )}>
        <div className="flex flex-col items-center justify-center gap-8">
          <div className="relative w-32 h-32 flex items-center justify-center">
            {/* Logo circle */}
            <div className="absolute inset-0 rounded-full border-4 border-white/20 flex items-center justify-center overflow-hidden">
              <div className="text-4xl font-bold text-white flex items-center">
                <CircleDot size={64} className="text-white" />
              </div>
            </div>
            
            {/* Progress circle */}
            <svg className="absolute inset-0 w-full h-full rotate-[-90deg]" viewBox="0 0 100 100">
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                fill="none" 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="6" 
              />
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                fill="none" 
                stroke="white" 
                strokeWidth="6" 
                strokeDasharray={`${2 * Math.PI * 45}`} 
                strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress / 100)}`}
                className="transition-all duration-300 ease-in-out"
              />
            </svg>
          </div>
          
          <div className="space-y-6 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-white animate-fade-in">
              Mucodec Digital Produit
            </h1>
            <p className="text-blue-100 animate-fade-in delay-200">
              Gestion de licences de produits numériques
            </p>
          </div>
          
          <div className="flex gap-2 mt-8">
            {[0, 1, 2].map((i) => (
              <div 
                key={i} 
                className={cn(
                  'w-3 h-3 rounded-full transition-colors duration-300',
                  progress >= (i + 1) * 33 ? 'bg-white' : 'bg-white/30'
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
